import React, { useState } from 'react';
import classNames from 'classnames';
import css from './Tooltip.module.css';

/**
 * Tooltip component that shows additional information on hover/focus
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} props.text - The text to display in the tooltip
 * @param {string} [props.position] - Position of tooltip: 'top', 'bottom', 'left', 'right' (default: 'top')
 * @param {string} [props.size] - Size of tooltip: 'small', 'medium', 'large' (default: 'medium')
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.iconClassName] - Additional CSS classes for the icon
 * @param {React.ReactNode} [props.children] - Optional custom trigger element
 * @returns {JSX.Element} The tooltip component
 */
const Tooltip = props => {
  const {
    text,
    position = 'top',
    size = 'medium',
    containerClassName,
    className,
    iconClassName,
    children,
  } = props;

  if (!text) return children;
  const [isVisible, setIsVisible] = useState(false);

  const handleMouseEnter = () => setIsVisible(true);
  const handleMouseLeave = () => setIsVisible(false);
  const handleFocus = () => setIsVisible(true);
  const handleBlur = () => setIsVisible(false);

  const tooltipClasses = classNames(
    css.tooltip,
    css[`position-${position}`],
    css[`size-${size}`],
    {
      [css.visible]: isVisible,
    },
    className
  );

  const triggerElement = children || (
    <svg xmlns="http://www.w3.org/2000/svg" style={{ fill: "none" }} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-alert-icon lucide-circle-alert"><circle cx="12" cy="12" r="10" /><line x1="12" x2="12" y1="8" y2="12" /><line x1="12" x2="12.01" y1="16" y2="16" /></svg>
  );

  return (
    <div
      className={classNames(css.tooltipContainer, containerClassName)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      tabIndex={0}
      role="button"
      aria-label={`Tooltip: ${text}`}
    >
      {triggerElement}
      <div className={tooltipClasses} role="tooltip">
        {text}
      </div>
    </div>
  );
};

export default Tooltip; 